import { jwtDecode } from "jwt-decode";
import { api } from "./api";
import getAccessToken from "./authToken";

const CLAIM_KEYS = {
  userid: ["internal_user_id", "userid", "userId", "id", "sub", "oid"],
  internal_user_id: ["internal_user_id"],
  rolename: ["role_name", "rolename", "roleName", "userRole", "role"],
  role_name: ["role_name"],
  username: ["username", "name", "unique_name"],
  emailId: ["emailId", "email", "preferred_username", "upn"],
  email: ["email", "preferred_username", "upn"],
  organizationId: ["organization_id", "organizationId", "organizationid", "orgId", "orgid"],
  organization_id: ["organization_id"],
  organizationName: ["organizationName", "organizationname", "orgName", "orgname"],
  clientId: ["clientId", "clientid"],
  clientName: ["clientName", "clientname"],
  gender: ["gender"],
  activeAccountId: ["active_account_id", "activeAccountId", "activeaccountid", "accountId", "accountid"],
  active_account_id: ["active_account_id"],
};

const getClaimValue = (claims = {}, keys = []) => {
  for (const key of keys) {
    const value = claims?.[key];

    if (Array.isArray(value) && value.length > 0) {
      return value[0];
    }

    if (value !== undefined && value !== null && value !== "") {
      return value;
    }
  }

  return undefined;
};

export const normalizeStoredUserData = (userData = {}) => {
  const normalizedData = { ...userData };
  const role =
    normalizedData.userRole ||
    normalizedData.roleName ||
    normalizedData.rolename ||
    normalizedData.role_name ||
    (Array.isArray(normalizedData.roles) ? normalizedData.roles[0] : normalizedData.roles) ||
    normalizedData.role;

  if (role) {
    normalizedData.rolename = role;
    normalizedData.userRole = normalizedData.userRole || role;
  }

  if (!normalizedData.userid) {
    normalizedData.userid =
      normalizedData.userid ||
      normalizedData.userId;
  }

  if (!normalizedData.userId && normalizedData.userid) {
    normalizedData.userId = normalizedData.userid;
  }

  if (!normalizedData.internal_user_id && normalizedData.userid) {
    normalizedData.internal_user_id = normalizedData.userid;
  }

  if (!normalizedData.emailId) {
    normalizedData.emailId =
      normalizedData.email ||
      normalizedData.preferred_username ||
      normalizedData.upn;
  }

  if (!normalizedData.email && normalizedData.emailId) {
    normalizedData.email = normalizedData.emailId;
  }

  if (!normalizedData.username) {
    normalizedData.username = normalizedData.name || normalizedData.unique_name;
  }

  if (!normalizedData.organizationId) {
    normalizedData.organizationId = normalizedData.organization_id;
  }

  if (!normalizedData.organization_id && normalizedData.organizationId) {
    normalizedData.organization_id = normalizedData.organizationId;
  }

  if (!normalizedData.activeAccountId) {
    normalizedData.activeAccountId = normalizedData.active_account_id;
  }

  if (!normalizedData.active_account_id && normalizedData.activeAccountId) {
    normalizedData.active_account_id = normalizedData.activeAccountId;
  }

  if (!normalizedData.role_name && normalizedData.rolename) {
    normalizedData.role_name = normalizedData.rolename;
  }

  return normalizedData;
};

export const getPostLoginRoute = (role) => {
  const normalizedRole = `${role || ""}`.trim().toLowerCase();
  return normalizedRole === "admin" ? "/audit" : "/dashboard";
};

export const decodeJwtUserData = (token) => {
  const claims = jwtDecode(token);
  const decodedUserData = Object.entries(CLAIM_KEYS).reduce((acc, [field, keys]) => {
    const value = getClaimValue(claims, keys);

    if (value !== undefined) {
      acc[field] = value;
    }

    return acc;
  }, {});

  const roleFromArray = Array.isArray(claims?.roles) ? claims.roles[0] : undefined;

  return normalizeStoredUserData({
    ...claims,
    ...decodedUserData,
    rolename: decodedUserData.rolename || roleFromArray,
    userDtls: decodedUserData.emailId || decodedUserData.email,
  });
};

const fetchUserDetailsFromSso = async (email) => {
  if (!email) {
    throw new Error("JWT email is missing. Unable to fetch user details.");
  }

  const backendResponse = await api.post("/Account/UserDetailsSSO", {
    username: email,
  });

  return normalizeStoredUserData(backendResponse.data || {});
};

const persistJwtTokenMetadata = (token, userData) => {
  const currentTimestamp = Date.now();
  const expirationTimestamp = Number(userData.exp) * 1000;

  sessionStorage.setItem("accessToken", token);

  if (Number.isFinite(expirationTimestamp)) {
    sessionStorage.setItem("tokenExpireTime", expirationTimestamp.toString());
  }

  sessionStorage.setItem("sessionStartTime", currentTimestamp.toString());
};

const mergeJwtAndUserDetails = (jwtUserData, backendUserData) => {
  const normalizedJwtData = normalizeStoredUserData(jwtUserData);
  const normalizedBackendData = normalizeStoredUserData(backendUserData);

  return normalizeStoredUserData({
    ...normalizedBackendData,
    emailId: normalizedJwtData.emailId || normalizedJwtData.email || normalizedBackendData.emailId,
    email: normalizedJwtData.email || normalizedJwtData.emailId || normalizedBackendData.email,
    userid: normalizedBackendData.userid || normalizedBackendData.userId || normalizedJwtData.userid,
    userId: normalizedBackendData.userId || normalizedBackendData.userid || normalizedJwtData.userid,
    internal_user_id:
      normalizedJwtData.internal_user_id ||
      normalizedJwtData.userid ||
      normalizedBackendData.internal_user_id,
    organizationId: normalizedJwtData.organizationId || normalizedBackendData.organizationId,
    organization_id:
      normalizedJwtData.organization_id ||
      normalizedJwtData.organizationId ||
      normalizedBackendData.organization_id,
    activeAccountId: normalizedJwtData.activeAccountId || normalizedBackendData.activeAccountId,
    active_account_id:
      normalizedJwtData.active_account_id ||
      normalizedJwtData.activeAccountId ||
      normalizedBackendData.active_account_id,
    rolename: normalizedJwtData.rolename || normalizedBackendData.rolename,
    userRole: normalizedJwtData.rolename || normalizedBackendData.userRole,
    role_name: normalizedJwtData.role_name || normalizedJwtData.rolename || normalizedBackendData.role_name,
    exp: normalizedJwtData.exp,
    iat: normalizedJwtData.iat,
    nbf: normalizedJwtData.nbf,
    iss: normalizedJwtData.iss,
    aud: normalizedJwtData.aud,
    userDtls: normalizedJwtData.emailId || normalizedJwtData.email,
  });
};

const persistResolvedSession = (token, userData) => {
  persistJwtTokenMetadata(token, userData);

  Object.entries(userData).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      const cleanKey = key.startsWith("http://schemas.") ? key.split("/").pop() : key;
      
      const valueToStore = typeof value === "object" ? JSON.stringify(value) : String(value);
      sessionStorage.setItem(cleanKey, valueToStore);
    }
  });

   sessionStorage.setItem("justLoggedIn", "true");

  return userData;
};

export const persistJwtSession = async (token) => {
  const jwtUserData = decodeJwtUserData(token);
  persistJwtTokenMetadata(token, jwtUserData);
  const backendUserData = await fetchUserDetailsFromSso(
    jwtUserData.emailId || jwtUserData.email
  );
  const mergedUserData = mergeJwtAndUserDetails(jwtUserData, backendUserData);

  return persistResolvedSession(token, mergedUserData);
};

export const fetchAndPersistValidUserJwt = async () => {
  const response = await api.get("/Account/validuser", {
    withCredentials: true,
    skipAuthHeader: true,
  });
  const jwt = response.data;

  if (!jwt) {
    return null;
  }

  return await persistJwtSession(jwt);
};

export const syncUserWithBackend = async (instance) => {
  try {
    const account = instance.getActiveAccount();
    if (!account) {
      return null;
    }

    await getAccessToken(instance);
    const userData = await fetchAndPersistValidUserJwt();

    if (!userData) {
      return { error: "UserNotFound" };
    }

    return userData;
  } catch (error) {
    console.error("Backend sync failed:", error);
    return { error: "SyncFailed", details: error };
  }
};

export const getUserInfo = (instance) => {
  const account = instance.getActiveAccount() || instance.getAllAccounts()[0];
  if (!account) return null;

  const claims = account.idTokenClaims || {};
  return {
    userid: claims.oid || account.localAccountId,
    username: claims.name,
    email: claims.preferred_username,
    roles: claims.roles || [],
  };
};

export const isAuthenticated = (instance) => {
  const account = instance.getActiveAccount() || instance.getAllAccounts()[0];
  return !!account;
};
 